// 図面の登録・編集の画面と図枠の登録・編集の画面で共通に使う、添付したファイルの一覧の行の組み立て
import { getUploadFileExtension } from './FileUpload.logic';

export interface ExistingImage {
  id: number;
  fileName: string;
  fileUrl: string;
}

export interface PendingImage {
  file: File;
  previewUrl: string;
}

// existing = アップロード済み、pending = 追加予定、deleted = アップロード済みだが保存時に削除する
export type ImageListItemState = 'existing' | 'pending' | 'deleted';

export interface ImageListItem {
  key: string;
  fileName: string;
  previewSrc: string;
  downloadUrl: string;
  state: ImageListItemState;
  existingId: number | null;
  previewUrl: string | null;
  thumbnailReady: boolean;
}

export function buildExistingImages(
  images: Array<{ id: number; file_name: string; file_url: string }>
): ExistingImage[] {
  return images.map((image) => ({
    id: image.id,
    fileName: image.file_name,
    fileUrl: image.file_url,
  }));
}

function toExistingItemState(imageId: number, removedExistingImageIds: number[]): ImageListItemState {
  if (removedExistingImageIds.includes(imageId)) {
    return 'deleted';
  }
  return 'existing';
}

// アップロード済み画像の縮小表示は、一度ダイアログで読み込んだものだけ実画像にする
export function buildImageListItems(
  existing: ExistingImage[],
  removedExistingImageIds: number[],
  loadedExistingImageIds: number[],
  pending: PendingImage[],
  apiUrl: string
): ImageListItem[] {
  const existingItems: ImageListItem[] = existing.map((image) => ({
    key: `existing-${image.id}`,
    fileName: image.fileName,
    previewSrc: apiUrl + image.fileUrl,
    downloadUrl: apiUrl + image.fileUrl,
    state: toExistingItemState(image.id, removedExistingImageIds),
    existingId: image.id,
    previewUrl: null,
    thumbnailReady: loadedExistingImageIds.includes(image.id),
  }));
  const pendingItems: ImageListItem[] = pending.map((image) => ({
    key: `pending-${image.previewUrl}`,
    fileName: image.file.name,
    previewSrc: image.previewUrl,
    downloadUrl: image.previewUrl,
    state: 'pending',
    existingId: null,
    previewUrl: image.previewUrl,
    thumbnailReady: true,
  }));
  const deletedItems = existingItems.filter((item) => item.state === 'deleted');
  const keptItems = existingItems.filter((item) => item.state === 'existing');
  return pendingItems.concat(deletedItems, keptItems);
}

// 保存時に残すアップロード済み画像。削除予定を除いたもの
export function selectKeptExistingImages(
  existing: ExistingImage[],
  removedExistingImageIds: number[]
): ExistingImage[] {
  return existing.filter((image) => !removedExistingImageIds.includes(image.id));
}

export interface StepDefinition {
  key: string;
  title: string;
  visible: boolean;
}

export interface StepLabels {
  [key: string]: string;
}

// 表示されている手順だけを数えて「1. 見出し」の形の文言を作る
export function buildStepLabels(steps: StepDefinition[]): StepLabels {
  const labels: StepLabels = {};
  const visibleSteps = steps.filter((step) => step.visible);
  for (let i = 0; i < visibleSteps.length; i++) {
    labels[visibleSteps[i].key] = `${i + 1}. ${visibleSteps[i].title}`;
  }
  return labels;
}

export type DrawingKind = 'dxf' | 'pdf' | 'image';

export function getDrawingKind(fileName: string): DrawingKind {
  const extension = getUploadFileExtension(fileName);
  if (extension === 'dxf') {
    return 'dxf';
  }
  if (extension === 'pdf') {
    return 'pdf';
  }
  return 'image';
}

// 図面の一覧行。新規添付は手元のファイル、既存はサーバー上のファイルを指す
export interface DrawingListItem {
  fileName: string;
  kind: DrawingKind;
  previewSrc: string;
  downloadUrl: string;
  isPending: boolean;
  thumbnailReady: boolean;
}

export function buildDrawingListItem(
  dxfFile: File | null,
  dxfPreviewUrl: string | null,
  existingDrawingName: string | null,
  existingDrawingUrl: string | null,
  apiUrl: string,
  existingThumbnailReady: boolean
): DrawingListItem | null {
  if (dxfFile !== null && dxfPreviewUrl !== null) {
    return {
      fileName: dxfFile.name,
      kind: getDrawingKind(dxfFile.name),
      previewSrc: dxfPreviewUrl,
      downloadUrl: dxfPreviewUrl,
      isPending: true,
      thumbnailReady: true,
    };
  }
  if (existingDrawingName !== null && existingDrawingUrl !== null) {
    return {
      fileName: existingDrawingName,
      kind: getDrawingKind(existingDrawingName),
      previewSrc: apiUrl + existingDrawingUrl,
      downloadUrl: apiUrl + existingDrawingUrl,
      isPending: false,
      thumbnailReady: existingThumbnailReady,
    };
  }
  return null;
}

// 図面の行は追加予定か既存かの 2 状態だけ。関連画像の行と同じ見た目の区分を使う
export function getDrawingRowState(isPending: boolean): ImageListItemState {
  if (isPending) {
    return 'pending';
  }
  return 'existing';
}

// 拡大表示の対象。図面と関連画像で共用する
export interface PreviewTarget {
  src: string;
  fileName: string;
  /** 拡大表示の窓の［ダウンロード］で保存するファイルのアドレス */
  downloadUrl: string;
  existingImageId: number | null;
  isDrawing: boolean;
  thumbnailReady: boolean;
}
